import { useEffect, useState } from "react";
import { type ClientCall, type Me, type Subscription, api, downloadInvoice } from "../lib/api";
import { date, dateTime, planLength, rupees } from "../lib/format";
import { CallTicket } from "../components/CallTicket";
import { Notice } from "../components/Shell";
import { TelegramJoin } from "../components/TelegramJoin";

interface OutboxEmail {
  id: string;
  kind: string;
  to: string[];
  subject: string;
  status: string;
  createdAt: string;
}

function serviceStatus(s: Subscription): string {
  if (s.status === "ACTIVE") return s.startsLater ? `Renewal starts ${date(s.periodStart)}` : `Active until ${date(s.periodEnd)}`;
  if (s.status === "EXPIRED") return `Ended ${date(s.periodEnd)}`;
  return "Cancelled; refund due";
}

export function Home({ onAddService }: { onAddService: (serviceId?: string) => void }) {
  const [me, setMe] = useState<Me | null>(null);
  const [view, setView] = useState<"active" | "closed">("active");
  const [calls, setCalls] = useState<ClientCall[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sandbox, setSandbox] = useState(false);
  const [reload, setReload] = useState(0);
  const [outbox, setOutbox] = useState<OutboxEmail[]>([]);

  useEffect(() => {
    api<Me>("/v1/me").then(setMe, () => setError("Could not load your account."));
    api<{ sandbox: boolean }>("/v1/sandbox/status").then(
      (s) => {
        setSandbox(s.sandbox);
        api<OutboxEmail[]>("/v1/sandbox/emails").then(setOutbox, () => setOutbox([]));
      },
      () => setSandbox(false),
    );
  }, []);

  const setTelegram = (id: string, telegram: Subscription["telegram"]) =>
    setMe((m) => (m ? { ...m, subscriptions: m.subscriptions.map((s) => (s.id === id ? { ...s, telegram } : s)) } : m));

  async function publishDemo() {
    await api("/v1/sandbox/demo-calls", { method: "POST" });
    setReload((n) => n + 1);
  }

  useEffect(() => {
    setCalls(null);
    const load = () => api<ClientCall[]>(`/v1/me/calls?view=${view}`).then(setCalls, () => setError("Could not load calls."));
    void load();
    // New calls appear without a reload; push notifications replace this in the mobile app.
    const t = window.setInterval(load, 30_000);
    return () => window.clearInterval(t);
  }, [view, reload]);

  const active = me?.subscriptions.filter((s) => s.status === "ACTIVE") ?? [];

  return (
    <div className="wide">
      <h1>{me?.name ? `Hello, ${me.name.split(" ")[0]}` : "Your calls"}</h1>
      {error && <Notice tone="bad">{error}</Notice>}
      {sandbox && (
        <Notice tone="warn">
          Test mode. Calls here are demos, not recommendations.{" "}
          <button className="link" onClick={() => void publishDemo()}>
            Publish a set of demo calls
          </button>
        </Notice>
      )}
      <div className="dash">
        <section aria-label="Calls">
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={view === "active"} onClick={() => setView("active")}>
              Active calls
            </button>
            <button role="tab" aria-selected={view === "closed"} onClick={() => setView("closed")}>
              Closed calls
            </button>
          </div>
          <div className="calls">
            {calls === null && <p className="muted">Loading…</p>}
            {calls?.length === 0 &&
              (view === "active" ? (
                <div className="empty">
                  {active.length ? "No open calls right now. New calls appear here the moment they are published." : "Subscribe to a service to start receiving calls."}
                </div>
              ) : (
                <div className="empty">Calls you received appear here once they close.</div>
              ))}
            {calls?.map((c) => <CallTicket key={c.callCode} call={c} />)}
          </div>
        </section>
        <aside className="side">
          <section>
            <h3>Your services</h3>
            <ul>
              {me?.subscriptions.map((s) => (
                <li key={s.id}>
                  <strong>{s.service}</strong>
                  <br />
                  <span className="muted">
                    {planLength(s.planDays)}. {serviceStatus(s)}
                  </span>
                  {!s.startsLater && <TelegramJoin subscriptionId={s.id} access={s.telegram} sandbox={sandbox} onChange={(t) => setTelegram(s.id, t)} />}
                  {s.canRenew && (
                    <button className="btn btn-small" onClick={() => onAddService(s.serviceId)}>
                      Renew {s.service}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <button className="link" onClick={() => onAddService()} style={{ marginTop: 10 }}>
              Add a service
            </button>
          </section>
          {me && me.invoices.length > 0 && (
            <section>
              <h3>Invoices</h3>
              <ul>
                {me.invoices.map((i) => (
                  <li key={i.invoiceNumber}>
                    <span className="num">{i.invoiceNumber}</span>
                    <br />
                    <span className="muted">
                      {i.service ? `${i.service}, ` : ""}
                      {date(i.paidAt)}, {rupees(i.totalPaise)} incl. GST
                    </span>
                    <br />
                    {i.pdfReady ? (
                      <button className="link small" onClick={() => void downloadInvoice(i.paymentId, i.invoiceNumber).catch((e: Error) => setError(e.message))}>
                        Download PDF
                      </button>
                    ) : (
                      <span className="small muted">PDF being prepared</span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {sandbox && outbox.length > 0 && (
            <section className="sandbox">
              <h3>Emails sent (test mode)</h3>
              <p className="small muted">Nothing is emailed in test mode. Open one to see what the client and your desk would receive.</p>
              <ul>
                {outbox.slice(0, 6).map((e) => (
                  <li key={e.id}>
                    <a href={`/v1/sandbox/emails/${e.id}`} target="_blank" rel="noopener noreferrer">
                      {e.kind === "ADMIN_SUBSCRIPTION_ALERT" ? "To RA desk" : "To client"}: {e.subject}
                    </a>
                    <br />
                    <span className="small muted">
                      {e.to.join(", ")} · {dateTime(e.createdAt)} · {e.status.toLowerCase()}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section>
            <h3>Need help?</h3>
            <p className="small muted">
              Write to support@finversify.com or call +91 99002 87333, Monday to Friday, 9 am to 6 pm. You can also raise a complaint on SEBI SCORES.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
